import { useMemo } from 'react'
import { brands as Brands } from '@/types/thermostatBrands'
import { useAppSelector } from '@/hooks'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import useMultiProgramEligibleDeviceTypes from '@/hooks/useMultiProgramEligibleDeviceTypes/useMultiProgramEligibleDeviceTypes'
import {
  selectIsEnrolledInThermostatSupportingProgram,
  selectProgram,
} from '@/reducers/programsNew'
import { filterThermostatBrandsForMPFlow } from './filterThermostatBrandsForMPFlow'
import { filterThermostatBrandsForProgram } from './filterThermostatBrandsForProgram'

/**
 * The thermostat brands the user may actually connect right now, after both brand filters:
 * the device-type-level Ecobee rule ({@link filterThermostatBrandsForMPFlow}) and the
 * program's brand allow-list ({@link filterThermostatBrandsForProgram}).
 *
 * Shared by the brand picker and by `ConnectThermostatFlow`, which skips the picker
 * entirely when only one brand survives — the two must not disagree about the list.
 * Callers should wait for `isLoadingOrIdle` to clear before acting on `brands`.
 */
export default function useEligibleThermostatBrands() {
  const { inFlow, multiProgramFlowProgramId } = useMultiProgramFlowControl()
  const { deviceTypes, isLoadingOrIdle } = useMultiProgramEligibleDeviceTypes(
    multiProgramFlowProgramId,
  )
  const isEnrolledInThermostatProgram = useAppSelector(
    selectIsEnrolledInThermostatSupportingProgram(),
  )
  const flowProgram = useAppSelector(
    selectProgram(multiProgramFlowProgramId ?? -1),
  )
  const eligibleThermostatBrands =
    flowProgram?.utility_program?.eligible_thermostat_brands

  const brands = useMemo(
    () =>
      filterThermostatBrandsForProgram(
        filterThermostatBrandsForMPFlow(
          Brands,
          inFlow,
          deviceTypes,
          isLoadingOrIdle,
          isEnrolledInThermostatProgram,
        ),
        eligibleThermostatBrands,
        inFlow,
      ),
    [
      inFlow,
      deviceTypes,
      isLoadingOrIdle,
      isEnrolledInThermostatProgram,
      eligibleThermostatBrands,
    ],
  )

  return { brands, isLoadingOrIdle }
}
